import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  useTheme,
  Divider,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import CustomSelect from "../../../components/ui/Select";
import Chip from "../../../components/ui/Chip";
import Modal from "../../../components/ui/Modal";
import Skeleton from "../../../components/ui/Skeleton";

const SESSION_TIMEOUT_SETTINGS_URL =
  "api/authentication/session-timeout-settings/";

const DURATION_OPTIONS = [
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours", recommended: true },
  { value: 240, label: "4 hours", recommended: true },
  { value: 360, label: "6 hours" },
  { value: 480, label: "8 hours" },
  { value: 720, label: "12 hours" },
];

const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = minutes / 60;
  return Number.isInteger(hours)
    ? `${hours} hour${hours === 1 ? "" : "s"}`
    : `${(minutes / 60).toFixed(1)} hours`;
};

interface SessionTimeoutConfig {
  enabled: boolean;
  timeoutMinutes: number;
}

interface SessionTimeoutSettingsResponse {
  enabled: boolean;
  timeout_minutes: number;
  last_updated_by_name: string | null;
  updated_at: string;
}

const DEFAULT_CONFIG: SessionTimeoutConfig = {
  enabled: false,
  timeoutMinutes: 240,
};

const SessionSettings: React.FC = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const [savedConfig, setSavedConfig] =
    useState<SessionTimeoutConfig>(DEFAULT_CONFIG);
  const [draftConfig, setDraftConfig] =
    useState<SessionTimeoutConfig>(DEFAULT_CONFIG);
  const [lastUpdatedBy, setLastUpdatedBy] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const isDirty = useMemo(
    () =>
      draftConfig.enabled !== savedConfig.enabled ||
      draftConfig.timeoutMinutes !== savedConfig.timeoutMinutes,
    [draftConfig, savedConfig],
  );

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await apiService.get<SessionTimeoutSettingsResponse>(
        SESSION_TIMEOUT_SETTINGS_URL,
      );
      const config: SessionTimeoutConfig = {
        enabled: response.data.enabled,
        timeoutMinutes: response.data.timeout_minutes,
      };
      setSavedConfig(config);
      setDraftConfig(config);
      setLastUpdatedBy(response.data.last_updated_by_name);
      setLastUpdatedAt(response.data.updated_at);
    } catch (err: unknown) {
      const errorResponse = (
        err as {
          response?: { status?: number; data?: { detail?: string } };
        }
      )?.response;
      const message =
        errorResponse?.status === 403
          ? "You do not have permission to view session settings."
          : errorResponse?.data?.detail || "Failed to load session settings.";
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = (enabled: boolean) => {
    setDraftConfig((prev) => ({ ...prev, enabled }));
  };

  const handleReset = () => {
    setDraftConfig(savedConfig);
  };

  const handleSaveClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiService.put<SessionTimeoutSettingsResponse>(
        SESSION_TIMEOUT_SETTINGS_URL,
        {
          enabled: draftConfig.enabled,
          timeout_minutes: draftConfig.timeoutMinutes,
        },
      );
      const config: SessionTimeoutConfig = {
        enabled: response.data.enabled,
        timeoutMinutes: response.data.timeout_minutes,
      };
      setSavedConfig(config);
      setDraftConfig(config);
      setLastUpdatedBy(response.data.last_updated_by_name);
      setLastUpdatedAt(response.data.updated_at);
      toast.success(
        config.enabled
          ? `Session timeout set to ${formatMinutes(config.timeoutMinutes)} from login.`
          : "Custom session timeout disabled — the platform default applies again.",
      );
      setConfirmOpen(false);
    } catch (err: unknown) {
      const errorResponse = (
        err as {
          response?: { status?: number; data?: { detail?: string } };
        }
      )?.response;
      const message =
        errorResponse?.status === 403
          ? "You do not have permission to change session settings."
          : errorResponse?.data?.detail ||
            "Failed to save session settings. Please try again.";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: colors.primary[100], mb: 0.5 }}
        >
          Session Timeout
        </Typography>
        <Typography variant="body2" sx={{ color: colors.primary[300] }}>
          Control how long a user stays logged in after they authenticate.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card
            title="Login-based Timeout"
            subtitle="Every user's token expires this many hours after their own login — not a fixed clock time."
            headerAction={
              !isLoading && !loadError ? (
                <Chip
                  status={draftConfig.enabled ? "success" : "neutral"}
                  label={draftConfig.enabled ? "Custom" : "Platform Default"}
                />
              ) : undefined
            }
          >
            {isLoading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="text" width="60%" />
              </Box>
            ) : loadError ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ color: colors.primary[300], mb: 2 }}
                >
                  {loadError}
                </Typography>
                <Button label="Retry" onClick={fetchSettings} />
              </Box>
            ) : (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                {/* Enable / disable toggle, built from the shared Button component
                  as a two-way segmented control. */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: colors.primary[300] }}
                  >
                    Timeout Mode
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                      label="Platform Default"
                      onClick={() => handleToggle(false)}
                      sx={{
                        flex: 1,
                        backgroundColor: !draftConfig.enabled
                          ? colors.primary[400]
                          : "transparent",
                        color: !draftConfig.enabled
                          ? colors.primary[900]
                          : colors.primary[300],
                        border: `1.5px solid ${colors.primary[400]}`,
                      }}
                    />
                    <Button
                      label="Custom Timeout"
                      onClick={() => handleToggle(true)}
                      sx={{
                        flex: 1,
                        backgroundColor: draftConfig.enabled
                          ? colors.primary[400]
                          : "transparent",
                        color: draftConfig.enabled
                          ? colors.primary[900]
                          : colors.primary[300],
                        border: `1.5px solid ${colors.primary[400]}`,
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    opacity: draftConfig.enabled ? 1 : 0.5,
                    pointerEvents: draftConfig.enabled ? "auto" : "none",
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <CustomSelect
                    label="Expire after"
                    value={draftConfig.timeoutMinutes}
                    onChange={(e) =>
                      setDraftConfig((prev) => ({
                        ...prev,
                        timeoutMinutes: Number(e.target.value),
                      }))
                    }
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                        {opt.recommended ? " (recommended)" : ""}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, color: colors.primary[300] }}
                  >
                    Example: if this is set to{" "}
                    {formatMinutes(draftConfig.timeoutMinutes)} and someone logs
                    in at 08:00, their session expires at{" "}
                    {(() => {
                      const expiry = new Date(2000, 0, 1, 8, 0);
                      expiry.setMinutes(
                        expiry.getMinutes() + draftConfig.timeoutMinutes,
                      );
                      return expiry.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      });
                    })()}{" "}
                    — regardless of when anyone else logged in.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: `${colors.primary[300]}20` }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: colors.primary[300] }}
                  >
                    {lastUpdatedBy
                      ? `Last changed by ${lastUpdatedBy}${
                          lastUpdatedAt
                            ? ` on ${new Date(lastUpdatedAt).toLocaleString()}`
                            : ""
                        }`
                      : "No changes made yet — currently using the platform default."}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                      label="Reset"
                      onClick={handleReset}
                      disabled={!isDirty || isSaving}
                      sx={{
                        backgroundColor: "transparent",
                        color: colors.primary[300],
                        border: `1.5px solid ${colors.primary[300]}40`,
                      }}
                    />
                    <Button
                      label={isSaving ? "Saving..." : "Save Changes"}
                      onClick={handleSaveClick}
                      disabled={!isDirty || isSaving}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Card>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: colors.primary[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.primary[900],
                    flexShrink: 0,
                  }}
                >
                  <AccessTimeIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.primary[100] }}
                  >
                    Rolling, per-user expiry
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.primary[300], mt: 0.5 }}
                  >
                    This timer starts at each person&apos;s own login. Two
                    people who log in an hour apart will be logged out an hour
                    apart too.
                  </Typography>
                </Box>
              </Box>
            </Card>

            <Card>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: "#2F3640",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    flexShrink: 0,
                  }}
                >
                  <NightsStayIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.primary[100] }}
                  >
                    Midnight logout — unaffected
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.primary[300], mt: 0.5 }}
                  >
                    Every account is still signed out at 00:00 regardless of
                    this setting. This page only controls the additional,
                    per-login timeout on top of that.
                  </Typography>
                  <Chip status="info" label="Always On" sx={{ mt: 1 }} />
                </Box>
              </Box>
            </Card>

            <Card>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ color: colors.primary[300], mt: 0.3 }}
                />
                <Typography variant="body2" sx={{ color: colors.primary[300] }}>
                  Changing this setting affects every user in the system on
                  their next request — it is not scoped to a single company or
                  role.
                </Typography>
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>

      <Modal
        open={confirmOpen}
        onClose={() => !isSaving && setConfirmOpen(false)}
        title="Confirm session timeout change"
        actions={
          <>
            <Button
              label="Cancel"
              onClick={() => setConfirmOpen(false)}
              disabled={isSaving}
              sx={{
                backgroundColor: "transparent",
                color: colors.primary[300],
                border: `1.5px solid ${colors.primary[300]}40`,
              }}
            />
            <Button
              label={isSaving ? "Applying..." : "Apply for everyone"}
              onClick={handleConfirmSave}
              disabled={isSaving}
            />
          </>
        }
      >
        <Typography variant="body2" sx={{ color: colors.primary[300] }}>
          {draftConfig.enabled
            ? `Every user's access token will now expire ${formatMinutes(draftConfig.timeoutMinutes)} after their own login. This applies immediately to all future logins.`
            : "Custom timeout will be turned off. Sessions will fall back to the platform default lifetime."}{" "}
          The nightly midnight logout is not affected either way.
        </Typography>
      </Modal>
    </Box>
  );
};

export default SessionSettings;
